import React, { useEffect, useState } from "react"
import { Link, useParams } from "react-router-dom"
import { ArrowLeft, Clock } from "lucide-react"
import { getPracticePaper } from "../../../api/studentPracticePapers"
import { STUDENT_AI_ASSESSOR_PATH } from "../../../component/student/config/studentNavItems"
import EmptyState from "../Questions/components/EmptyState"
import { paperDisplayTitle, type PaperSession } from "../PracticePapers/types"
import ScoreRing from "./components/ScoreRing"
import PaperQuestionDetail from "./components/PaperQuestionDetail"
import PaperQuestionPicker from "./components/PaperQuestionPicker"
import { formatAssessorTime, pctOf } from "./utils/format"
import "./AiAssessorPage.style.scss"
import "./PaperAssessorPage.style.scss"

const paperTotals = (paper: PaperSession) => {
  const score = paper.questions.reduce((sum, q) => sum + (q.score ?? 0), 0)
  const max = paper.questions.reduce((sum, q) => sum + q.maxScore, 0)
  return { score, max, pct: pctOf(score, max) }
}

const PaperAssessorPage = () => {
  const { paperId = "" } = useParams()
  const [paper, setPaper] = useState<PaperSession | null>(null)
  const [selected, setSelected] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState("")

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError("")
    setPaper(null)
    setSelected(0)
    getPracticePaper(paperId)
      .then((res) => {
        if (cancelled) return
        if (!res.data?.success || !res.data.data) {
          throw new Error(res.data?.message || "Could not load this paper")
        }
        setPaper(res.data.data as PaperSession)
      })
      .catch(() => {
        if (!cancelled) setError("Could not load this paper")
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [paperId])

  const question = paper?.questions[selected]
  const totals = paper ? paperTotals(paper) : null

  return (
    <div className="ai-assessor-page">
      <div className="ai-assessor-page__inner paper-assessor">
        <Link className="paper-assessor__back" to={STUDENT_AI_ASSESSOR_PATH}>
          <ArrowLeft size={16} /> AI Assessor
        </Link>

        {loading && <EmptyState title="Loading feedback…" />}
        {!loading && error && (
          <EmptyState title="Couldn’t load this paper" description={error} />
        )}
        {!loading && paper && !question && (
          <EmptyState title="This paper has no questions" />
        )}

        {paper && question && (
          <>
            <div className="paper-assessor__title">
              <ScoreRing pct={totals?.pct ?? 0} />
              <div>
                <h1>{paperDisplayTitle(paper)}</h1>
                <p>
                  {totals?.score}/{totals?.max} marks
                  {paper.totalTimeSpentSeconds > 0 && (
                    <>
                      {" "}
                      · <Clock size={12} />{" "}
                      {formatAssessorTime(paper.totalTimeSpentSeconds)}
                    </>
                  )}
                </p>
              </div>
            </div>

            <PaperQuestionPicker
              questions={paper.questions}
              selected={selected}
              onSelect={setSelected}
            />
            <PaperQuestionDetail
              index={selected}
              total={paper.questions.length}
              question={question}
              onPrev={() => setSelected((index) => Math.max(0, index - 1))}
              onNext={() =>
                setSelected((index) =>
                  Math.min(paper.questions.length - 1, index + 1)
                )
              }
            />
          </>
        )}
      </div>
    </div>
  )
}

export default PaperAssessorPage
